import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { computeBankEligibility } from "@/lib/eligibility";
import { inrFull } from "@/lib/format";
import { Landmark, CheckCircle2, XCircle } from "lucide-react";

// Compare Banks card shown inside a lead's detail — instantly shows which banks
// this customer qualifies for based on their income + requested loan amount.
export function QualifyingBanks({ lead }) {
  const [income, setIncome] = useState(Number(lead?.monthly_income) || "");
  const { data: lenders = [], isLoading } = useQuery({ queryKey: ["lenders"], queryFn: async () => (await api.get("/lenders")).data });

  const requested = Number(lead?.loan_amount) || 0;
  const results = computeBankEligibility(lenders, { income: Number(income) || 0, rate: 14, months: 60, foir: 50, requestedAmount: requested });
  const qualifyCount = results.filter((r) => r.qualifies).length;
  const hasIncome = Number(income) > 0;

  return (
    <div className="mt-5" data-testid="qualifying-banks">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1"><Landmark className="h-3.5 w-3.5" /> Compare Banks {hasIncome && <span className="text-emerald-600 normal-case font-medium">· {qualifyCount} qualify</span>}</div>

      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs text-slate-500">Monthly income ₹</span>
        <Input type="number" value={income} onChange={(e) => setIncome(e.target.value)} placeholder="Enter income" className="h-8 w-40" data-testid="qbanks-income-input" />
        {requested > 0 && <span className="text-xs text-slate-500">· needs {inrFull(requested)}</span>}
      </div>

      {!hasIncome && <p className="text-sm text-slate-400">Enter the customer's monthly income to see matching banks.</p>}

      {hasIncome && (
        <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1" data-testid="qbanks-list">
          {isLoading && <p className="text-sm text-slate-400">Loading banks…</p>}
          {results.map((r) => (
            <div key={r.id || r.code} data-testid={`qbank-${r.code}`} className={`flex items-center justify-between text-sm border rounded-lg px-3 py-2 ${r.qualifies ? "border-emerald-200 dark:border-emerald-900/60 bg-emerald-50/40 dark:bg-emerald-950/20" : "border-slate-100 dark:border-slate-800 opacity-70"}`}>
              <div className="flex items-center gap-2">
                {r.qualifies ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <XCircle className="h-4 w-4 text-slate-300 dark:text-slate-600" />}
                <span className="font-medium text-slate-800 dark:text-slate-200">{r.name}</span>
              </div>
              <div className="text-right">
                <div className={`font-num font-semibold ${r.qualifies ? "text-blue-700 dark:text-blue-400" : "text-slate-400"}`}>{r.meetsIncome ? inrFull(r.eligibleAmount) : "—"}</div>
                {!r.meetsIncome ? <Badge variant="outline" className="text-[9px]">min ₹{(r.min_income || 0).toLocaleString("en-IN")}</Badge> : (!r.meetsAmount && <Badge variant="outline" className="text-[9px] text-amber-600">below request</Badge>)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
